<template>
  <div class="upcoming-courses-page">
    <HeroSection v-if="primaryCourse" :course="primaryCourse" />
    <div class="course-cards container">
      <div>
        <h1 class="h4 col-10 offset-1">
          Upcoming courses
        </h1>
        <div class="row">
          <div
            v-for="course in courses"
            :key="course.id"
            class="upcoming-course-card col col-10 offset-1 sm-6"
          >
            <Card :course="course" heading="h2" @click.native="coursePreview(course.id)" />
          </div>
        </div>
      </div>
    </div>
    <div v-if="$store.state.auth.user" class="container next">
      <div class="col-10 offset-1 see-next-card">
        <h5>What Would You Like to See Next?</h5>
        <form id="next" :class="{ 'vote': userVotes }" @submit="handleSubmit">
          <p v-for="option in options" :key="option.id">
            <input
              :id="option.id"
              v-model="selected"
              :disabled="userVotes"
              type="radio"
              :value="option.id"
              @change="customOption = ''"
            >
            <label :for="option.id">{{ option.name }}</label>
            <LineProgress
              v-if="userVotes && !option.isCustom"
              class="line-progress"
              :width="option.percents || 0"
            >
              <span class="line-progress-text">{{ option.percents || 0 }}%</span>
            </LineProgress>
          </p>
          <p v-if="!userVotes">
            <input
              v-model="customOption"
              type="text"
              placeholder="Your Option"
              @keypress="selected = ''"
            >
          </p>
          <button v-if="!userVotes" class="btn-primary" type="submit">
            Submit
          </button>
          <p v-if="userVotes" class="total-votes">
            {{ totalVotes }} Total Votes
          </p>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { isEmpty } from 'lodash-es';
import global from '../mixins/global';
import HeroSection from './HeroSection.vue';
import Card from './Card.vue';
import LineProgress from '../LineProgress.vue';

export default {
  components: {
    HeroSection,
    Card,
    LineProgress,
  },
  mixins: [global],
  props: {
    data: { type: Object, required: true },
  },
  data() {
    return {
      courses: this.data.courses,
      options: this.data.options,
      count: this.data.count,
      totalVotes: this.data.totalVotes,
      userVotes: isEmpty(this.data.userVotes) ? false : this.data.userVotes,
      primaryCourse: isEmpty(this.data.primaryCourse) ? false : this.data.primaryCourse,
      customOption: '',
      selected: isEmpty(this.data.userVotes) ? 0 : this.data.userVotes.optionId,
      page: this.data.page,
    };
  },
  head() {
    const site = this.$config.project === 'site';
    let linkedData;
    let metaProps;
    if (site && this.page) {
      linkedData = this.$linkedDataLoad([
        { key: 'WebSite' },
        { key: 'WebPage', values: { ...this.page } },
      ]);
      metaProps = this.metaProps({ ...this.page, metaImage: '' });
    }
    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: site ? [
        { rel: 'canonical', href: this.canonicalURL() },
      ] : [],
      meta: metaProps,
      script: site ? [linkedData] : [],
    };
  },
  created() {
    if (this.page) {
      this.page.fullPath = `${this.$config.siteUrl}${this.$route.fullPath}`;
    }
  },
  mounted() {
    if (this.$route.hash.length) {
      this.coursePreview(+this.$route.hash.replace('#', ''));
    }
  },
  methods: {
    async coursePreview(course) {
      this.$store.dispatch('modals/open', { name: 'upcoming-course', upcomingCourseId: course });
    },
    async handleSubmit(event) {
      event.preventDefault();
      try {
        await this.$axios.$post('/upcoming-courses/vote', {
          option: this.selected,
          custom: this.customOption,
          optionGroup: this.options[0].group,
        });
        dataLayer.push({ event: 'CourseSuggestion' });
        const {
          courses,
          options,
          count,
          userVotes,
          totalVotes,
          primaryCourse,
        } = await this.$axios.$get('/upcoming-courses');

        this.courses = courses;
        this.options = options;
        this.count = count;
        this.totalVotes = totalVotes;
        this.userVotes = isEmpty(userVotes) ? false : userVotes;
        this.primaryCourse = isEmpty(primaryCourse) ? false : primaryCourse;
        this.selected = this.userVotes ? this.userVotes.optionId : 0;

        this.$tracking.analytics().track('upcoming_survey_submitted', { upcoming_survey_answer: this.options.find((o) => o.id === this.selected).name });
      } catch (e) {
        console.error();
      }
    },
  },
};
</script>

<style lang="scss">
.upcoming-courses-page {
  background: var(--main-light-shade-1);
  padding-bottom: var(--spacing-120);

  .course-cards {
    margin-bottom: var(--spacing-24);

    .h4 {
      margin-top: var(--spacing-40);
      margin-bottom: var(--spacing-16);
    }

    .upcoming-course-card {
      margin-top: var(--spacing-8);
      margin-bottom: var(--spacing-8);
    }

    a {
      color: inherit;
      cursor: pointer;
    }

    hr {
      margin: var(--spacing-80) 0;
      height: 1px;
      background-color: var(--main-light-shade-3);
      border: none;
    }

    @include bp($bp-mobile) {
      margin-bottom: var(--spacing-lg);

      .h4 {
        margin-top: var(--spacing-lg);
        font-size: 21px;
        line-height: 26px;
      }

      hr {
        display: none;
      }
    }
  }

  .see-next-card {
    background: var(--main-light);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    padding: 44px 52px 52px;

    h5 {
      margin-top: 0;
      margin-bottom: var(--spacing-36);
    }

    form {
      .line-progress {
        .slot {
          position: unset;
        }
      }

      p {
        margin: var(--spacing-24) 0 0;

        [type="radio"]:checked,
        [type="radio"]:not(:checked) {
          position: absolute;
          left: -9999px;
        }

        [type="radio"]:checked + label,
        [type="radio"]:not(:checked) + label {
          position: relative;
          padding-left: 28px;
          cursor: pointer;
          line-height: 20px;
          display: inline-block;
          color: var(--main-dark-shade-3);
        }

        [type="radio"]:checked + label:before,
        [type="radio"]:not(:checked) + label:before {
          content: "";
          position: absolute;
          left: 0;
          top: 0;
          width: 18px;
          height: 18px;
          border: 1px solid var(--main-light-shade-4);
          border-radius: 100%;
          background: var(--main-light);
        }

        [type="radio"]:checked + label:after,
        [type="radio"]:not(:checked) + label:after {
          content: "";
          width: 12px;
          height: 12px;
          background: var(--accent);
          position: absolute;
          top: 4px;
          left: 4px;
          border-radius: 100%;
          -webkit-transition: all 0.2s ease;
          transition: all 0.2s ease;
        }

        [type="radio"]:checked + label:before {
          border: 1px solid var(--accent);
        }

        [type="radio"]:not(:checked) + label:after {
          opacity: 0;
          -webkit-transform: scale(0);
          transform: scale(0);
        }

        [type="radio"]:checked + label:after {
          opacity: 1;
          -webkit-transform: scale(1);
          transform: scale(1);
        }

        [type="text"] {
          width: 50%;
          height: 44px;
        }
      }

      .btn-primary {
        width: 200px;
        height: 48px;
        margin-top: var(--spacing-36);
      }

      .total-votes {
        text-align: right;
        font-size: 14px;
        color: var(--main-dark-shade-0);
        font-weight: 500;
      }
    }

    @include bp($bp-mobile) {
      padding-left: var(--spacing-32);
      padding-right: var(--spacing-32);

      form {
        p {
          margin: var(--spacing-sm) 0;

          [type="radio"]:checked + label,
          [type="radio"]:not(:checked) + label {
            line-height: 27px;
          }

          [type="radio"]:checked + label:before,
          [type="radio"]:not(:checked) + label:before {
            top: 6px;
            height: 16px;
            width: 16px;
          }

          [type="radio"]:checked + label:after,
          [type="radio"]:not(:checked) + label:after {
            top: 9px;
            left: 3px;
          }
        }

        .btn-primary {
          width: 100%;
        }

        .total-votes {
          margin: var(--spacing-16) 0 0;
          font-weight: 500;
        }
      }
    }
  }

  @include bp($bp-laptop) {
    .full-page-layout .container .row > div .h3 {
      font-size: 32px;
    }

    .course-cards .h4 {
      font-size: 24px;
    }
  }

  @include bp($bp-mobile) {
    padding-bottom: 32px;
  }
}

.fa {
  .upcoming-courses-page {
    background: var(--main-light-shade-2);
  }

  .line-progress-wrapper {
    .line-progress-bar {
      background: var(--main-light-shade-3);
    }
  }

  @include bp($bp-mobile) {
    .upcoming-courses-page {
      .course-cards {
        padding-bottom: var(--spacing-lg);
        margin-bottom: 0;

        .upcoming-course-card {
          margin-top: 2px;
          margin-bottom: 2px;

          .course-link {
            border-radius: 12px;

            .card-img {
              border-radius: 12px 12px 0 0;
            }
          }
        }
      }

      .see-next-card form {
        width: 100%;
      }
    }
  }
}
</style>
